import { JumperTerm, MetadataTableName, SecondaryStatus, TenantConfig, TermCode } from '@bechtel/sr-types';
import { CellClassParams, ColDef, GridApi } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css'; // Core grid CSS, always needed
import 'ag-grid-community/styles/ag-theme-alpine.css'; // Optional theme CSS
import { filter, find, get, has, includes, isEmpty, isEqual, isNil, map, omit, startsWith } from 'lodash';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useAppUser } from '~/components';
import { ClientGrid } from '~/components/organisms/grid';
import { useSelectListQuery } from '~/hooks';
import { getFilterByTermCodeLevel } from '../utils';

interface JumperTermProps {
  terms: JumperTerm[];
  setTerms: React.Dispatch<React.SetStateAction<JumperTerm[]>>;
  onGridReady: (api: GridApi, name: string) => void;
  onRowClick: (data: JumperTerm) => void;
  initialTerms: JumperTerm[];
  multipleFromEquipments?: boolean;
  multipleToEquipments?: boolean;
  gridApi: GridApi | undefined;
  canSetTerm: boolean;
  secondaryStatus: string;
  engineeringStatus: string;
}

export const JumperTermList: FC<JumperTermProps> = ({
  terms,
  setTerms,
  onGridReady,
  onRowClick,
  initialTerms,
  gridApi,
  canSetTerm,
  secondaryStatus,
  engineeringStatus,
}) => {
  const { userAccount } = useAppUser();
  const tenantConfig: TenantConfig = get(userAccount, 'tenantConfig') as TenantConfig;
  const theme = useTheme();
  //

  const [getSelectListDataTermCode] = useSelectListQuery('CABLE_TERM.FROM_CODE');
  //
  useEffect(() => {
    if (gridApi) {
      gridApi.refreshCells({ force: true });
    }
  }, [terms, gridApi]);

  const defaultColDef: ColDef = useMemo(() => {
    return {
      sortable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
    };
  }, []);

  const changeControlCheck = tenantConfig?.isJumperChangeControlUsed && Number(engineeringStatus) > 0;

  const getCellStyle = useCallback(
    (params: CellClassParams) => {
      //@ts-ignore readonly blue need to be added to GDS theme type
      const changedCellStyle = { backgroundColor: theme.colors.blue[100], color: theme.colors.readonlyBlue };
      const readOnlyCellStyle = { backgroundColor: theme.colors.grey[100] };
      const changeControlCellStyle = { backgroundColor: theme.colors.yellow[100] };

      //@ts-ignore readonly blue need to be added to GDS theme type
      const countErrorCellStyle = { color: theme.colors.readonlyRed };

      const originalRow = find(initialTerms, (term: JumperTerm) => term.id === params?.data?.id) as JumperTerm;
      const row = params?.data;
      const fieldName = params.column.getColDef().field;

      // customer and vendor count columns
      if (fieldName === 'jumperTermView.fromVendorCount') {
        if (
          originalRow &&
          originalRow.jumperTermView &&
          (originalRow.jumperTermView.fromVendorCount as number) > tenantConfig.maxWiresPerPoint
        ) {
          return countErrorCellStyle;
        } else {
          return readOnlyCellStyle;
        }
      }

      if (fieldName === 'jumperTermView.fromCustomerCount') {
        if (
          originalRow &&
          originalRow.jumperTermView &&
          (originalRow.jumperTermView.fromCustomerCount as number) > tenantConfig.maxWiresPerPoint
        ) {
          return countErrorCellStyle;
        } else {
          return readOnlyCellStyle;
        }
      }

      if (fieldName === 'jumperTermView.toCustomerCount') {
        if (
          originalRow &&
          originalRow.jumperTermView &&
          (originalRow.jumperTermView.toCustomerCount as number) > tenantConfig.maxWiresPerPoint
        ) {
          return countErrorCellStyle;
        } else {
          return readOnlyCellStyle;
        }
      }
      if (fieldName === 'jumperTermView.toVendorCount') {
        if (
          originalRow &&
          originalRow.jumperTermView &&
          (originalRow.jumperTermView.toVendorCount as number) > tenantConfig.maxWiresPerPoint
        ) {
          return countErrorCellStyle;
        } else {
          return readOnlyCellStyle;
        }
      }

      // secondary status disable blocks and points column
      if (
        !canSetTerm ||
        (secondaryStatus === SecondaryStatus.N &&
          includes(
            [
              `jumperTermView.fromBlockTag`,
              `jumperTermView.fromPointTag`,
              'fromSide',
              `jumperTermView.toBlockTag`,
              `jumperTermView.toPointTag`,
              'toSide',
            ],
            fieldName
          ))
      ) {
        return readOnlyCellStyle;
      }

      // from fields change tracking
      if (
        includes(
          [
            'fromWire',
            'fromSide',
            'fromCode',
            'description',
            `jumperTermView.fromBlockTag`,
            `jumperTermView.fromPointTag`,
          ],
          fieldName
        )
      ) {
        if (!canSetTerm) return readOnlyCellStyle;
        if (row && originalRow) {
          const {
            fromWire,
            fromSide,
            fromCode,
            description,
            jumperTermView: { fromPointTag, fromBlockTag },
          } = row;

          const {
            fromWire: origFromWire,
            fromSide: origFromSide,
            fromCode: origFromCode,
            description: origDescription,
          } = originalRow;
          const origFromBlockTag = originalRow.jumperTermView?.fromBlockTag;
          const origFromPointTag = originalRow.jumperTermView?.fromPointTag;

          if (changeControlCheck) return changeControlCellStyle;

          if (
            fromWire !== origFromWire ||
            fromBlockTag !== origFromBlockTag ||
            fromPointTag !== origFromPointTag ||
            fromSide !== origFromSide ||
            fromCode !== origFromCode ||
            description != origDescription
          ) {
            return changedCellStyle;
          }
        }
      }
      // to fields change tracking
      if (
        startsWith(fieldName, 'to') ||
        includes([`jumperTermView.toBlockTag`, `jumperTermView.toPointTag`], fieldName)
      ) {
        if (!canSetTerm) return readOnlyCellStyle;
        if (row && originalRow) {
          const {
            toWire,
            toSide,
            toCode,
            description,
            jumperTermView: { toPointTag, toBlockTag },
          } = row;

          const {
            toWire: origToWire,
            toSide: origToSide,
            toCode: origToCode,
            description: origDescription,
          } = originalRow;
          const origToBlockTag = originalRow.jumperTermView?.toBlockTag;
          const origToPointTag = originalRow.jumperTermView?.toPointTag;

          if (changeControlCheck) return changeControlCellStyle;

          if (
            toWire !== origToWire ||
            toBlockTag !== origToBlockTag ||
            toPointTag !== origToPointTag ||
            toSide !== origToSide ||
            toCode !== origToCode ||
            description != origDescription
          ) {
            return changedCellStyle;
          }
        }
      }
      // other fields change tracking
      if (row && originalRow && fieldName && row[fieldName] !== originalRow[fieldName]) {
        return changedCellStyle;
      }

      return { backgroundColor: 'initial' };
    },
    [initialTerms, theme, secondaryStatus, tenantConfig, changeControlCheck, canSetTerm]
  );

  const onTermEdited = useCallback(
    (editedRow: JumperTerm) => {
      // omit jumperTermView so has function will not detect jumperTermView:{fromEquipmentTag} vs cableTemView.fromEquipmentTag
      const src = omit(editedRow, 'jumperTermView');
      const updatedRow: JumperTerm = {
        ...editedRow,
        fromWire: isNil(editedRow.fromWire) || isEmpty(editedRow.fromWire) ? 'SPARE' : editedRow.fromWire,
        jumperTermView: {
          ...editedRow.jumperTermView,
          fromBlockTag: has(src, 'jumperTermView.fromBlockTag')
            ? src['jumperTermView.fromBlockTag']
            : editedRow.jumperTermView?.fromBlockTag,
          fromPointTag: has(src, 'jumperTermView.fromPointTag')
            ? src['jumperTermView.fromPointTag']
            : editedRow.jumperTermView?.fromPointTag,
          toBlockTag: has(src, 'jumperTermView.toBlockTag')
            ? src['jumperTermView.toBlockTag']
            : editedRow.jumperTermView?.toBlockTag,
          toPointTag: has(src, 'jumperTermView.toPointTag')
            ? src['jumperTermView.toPointTag']
            : editedRow.jumperTermView?.toPointTag,
        },
      };
      setTerms((prevRows) => map(prevRows, (row) => (isEqual(row.id, editedRow.id) ? updatedRow : row)));
    },
    [setTerms]
  );

  const customColDefs = useMemo(() => {
    const colDef = {
      fromWire: {
        editable: canSetTerm && !changeControlCheck,
        cellStyle: getCellStyle,
      },
      fromCode: {
        editable: canSetTerm && !changeControlCheck,
        hide: tenantConfig.termCodesLevel === 0,
        cellStyle: getCellStyle,
        picklistFilter: () => getFilterByTermCodeLevel(tenantConfig?.termCodesLevel),
      },
      fromSide: {
        editable:
          canSetTerm &&
          ((secondaryStatus && secondaryStatus === SecondaryStatus.N) || changeControlCheck ? false : true),
        cellStyle: getCellStyle,
      },
      'jumperTermView.fromBlockTag': {
        editable:
          canSetTerm &&
          ((secondaryStatus && secondaryStatus === SecondaryStatus.N) || changeControlCheck ? false : true),
        cellStyle: getCellStyle,
      },
      'jumperTermView.fromPointTag': {
        editable:
          canSetTerm &&
          ((secondaryStatus && secondaryStatus === SecondaryStatus.N) || changeControlCheck ? false : true),
        cellStyle: getCellStyle,
      },
      'jumperTermView.fromVendorCount': {
        cellStyle: getCellStyle,
      },
      'jumperTermView.fromCustomerCount': {
        cellStyle: getCellStyle,
      },
      toWire: {
        editable: canSetTerm && !changeControlCheck,
        hide: !tenantConfig?.isToWireUsed,
        cellStyle: getCellStyle,
      },
      toSide: {
        editable:
          canSetTerm &&
          ((secondaryStatus && secondaryStatus === SecondaryStatus.N) || changeControlCheck ? false : true),
        cellStyle: getCellStyle,
      },
      toCode: {
        editable: !changeControlCheck,
        hide: tenantConfig.termCodesLevel === 0,
        cellStyle: getCellStyle,
        picklistFilter: () => getFilterByTermCodeLevel(tenantConfig?.termCodesLevel),
      },
      'jumperTermView.toBlockTag': {
        editable:
          canSetTerm &&
          ((secondaryStatus && secondaryStatus === SecondaryStatus.N) || changeControlCheck ? false : true),
        cellStyle: getCellStyle,
      },
      'jumperTermView.toPointTag': {
        editable:
          canSetTerm &&
          ((secondaryStatus && secondaryStatus === SecondaryStatus.N) || changeControlCheck ? false : true),
        cellStyle: getCellStyle,
      },
      'jumperTermView.toVendorCount': {
        cellStyle: getCellStyle,
      },
      'jumperTermView.toCustomerCount': {
        cellStyle: getCellStyle,
      },
      description: {
        hide: !tenantConfig?.isWireDescAllowed,
        cellStyle: getCellStyle,
      },
    };
    return colDef;
  }, [
    getCellStyle,
    tenantConfig.termCodesLevel,
    tenantConfig?.isToWireUsed,
    tenantConfig?.isWireDescAllowed,
    secondaryStatus,
    changeControlCheck,
    canSetTerm,
  ]);

  const onGridInitialize = (api: GridApi) => {
    api.autoSizeAllColumns();
    onGridReady && onGridReady(api, 'terms');
  };
  const validateTermCode = useCallback(async (value: string, getSelectListData: () => Promise<any>, termCodesLevel) => {
    const {
      data: {
        getSelectListData: { items },
      },
    } = await getSelectListData();

    const validateTermCodeLevel = (value) => {
      switch (termCodesLevel) {
        case TermCode.C:
          return value?.startsWith('C');
        case TermCode.JC:
          return value?.startsWith('J');
        case TermCode.PRJC:
          return value?.startsWith('P');
        case TermCode.RJC:
          return value?.startsWith('R');
        default:
          return false;
      }
    };
    const allowedItems = filter(items, (obj) => validateTermCodeLevel(obj.column1));
    return allowedItems.some((item) => item.column1 === value);
  }, []);

  const validationFns = useMemo(() => {
    return {
      fromCode: async (value) => {
        return await validateTermCode(value, getSelectListDataTermCode, tenantConfig?.termCodesLevel);
      },
      toCode: async (value) => {
        return await validateTermCode(value, getSelectListDataTermCode, tenantConfig?.termCodesLevel);
      },
    };
  }, [getSelectListDataTermCode, tenantConfig?.termCodesLevel, validateTermCode]);

  if (!terms || terms.length === 0) return;

  return (
    <ClientGrid
      getRowId={(data) => data?.id}
      originalRowData={initialTerms}
      name={MetadataTableName.JUMPER_TERM}
      rowSelection="single"
      hideLayoutHeader={true}
      canCreateLayout={false}
      systemLayoutName={'jumper.jumperTerms'}
      rowData={terms}
      disableNewRow={true}
      skipPermission={true}
      customDefaultColDef={defaultColDef}
      hideFloatingFilter={true}
      onGridInitialize={onGridInitialize}
      readOnly={!canSetTerm}
      customColDefs={customColDefs}
      onRowEdited={onTermEdited}
      onClick={onRowClick}
      containerHorizontalSpacing="100"
      validationFns={validationFns}
      hideCheckBoxColumn
    ></ClientGrid>
  );
};

export default JumperTermList;
